import {
  Button,
  Content,
  Flex,
  FlyoutMenu,
  FlyoutMenuItem,
  StrokeIcon,
  usePlatformInfo,
  usePlatformProps,
} from '@acsbe/ui';
import { useState } from 'react';
import styled from 'styled-components';
import WidgetIcon from '../../assets/widget.svg';
import {
  APPLICATION_MODES,
  useApplicationViewState,
  useCompleteLogout,
  useDomainSelection,
  useMerchantSession,
} from '../../context';
import { CancelPlanPopup } from '../../ui/CancelPlanPopup';
import { TurnOffWidgetPopup } from '../../ui/TurnOffWidgetPopup';
import { CMS } from '../../utils/constants';
import { Logger } from '../../utils/logger';

const FlyoutActionMenu = () => {
  const p = usePlatformProps();
  const { isMobile, uptoPhablet } = usePlatformInfo();
  const { completeLogout: onLogout } = useCompleteLogout();

  const { licenseId, accountId, licenseDetails } = useDomainSelection();
  const {
    merchantDetails,
    toggleScript,
    redirectUrl: _redirectUrl,
  } = useMerchantSession();
  const { applicationMode } = useApplicationViewState();
  const redirectUrl = _redirectUrl || document.referrer;

  const [showCancelPlanPopup, setShowCancelPlanPopup] = useState(false);
  const [showTurnOffWidgetPopup, setShowTurnOffWidgetPopup] = useState(false);

  const turnOffWidget = () => {
    setShowTurnOffWidgetPopup(true);
  };

  const turnOnWidget = () => {
    toggleScript(true);
  };

  const handleStayAccessible = () => {
    setShowTurnOffWidgetPopup(false);
  };

  const handleTurnOffWidget = () => {
    Logger.log('Turning off widget...');
    toggleScript(false);
    setShowTurnOffWidgetPopup(false);
  };

  const MenuItems = () => {
    const items = [];
    if (applicationMode !== APPLICATION_MODES.LIMITED) {
      items.push(
        <MenuItem
          compact
          onClick={() => {
            window.open(
              `${import.meta.env.VITE_DASHBOARD_URL}/app/widget-license/${licenseId}?switchAccount=${accountId}&redirect-url=${redirectUrl}`,
              '_blank',
            );
          }}
          data-mixpanel="manage-license"
        >
          <StrokeIcon size="md" name="Gear" />
          Manage license
        </MenuItem>,
      );
      items.push(
        <MenuItem
          compact
          onClick={() => {
            window.open(
              `${import.meta.env.VITE_DASHBOARD_URL}/app/account`,
              '_blank',
            );
          }}
          data-mixpanel="account-settings"
        >
          <StrokeIcon size="md" name="User" />
          Account settings
        </MenuItem>,
      );
      if (
        merchantDetails?.source === CMS.WOOCOMMERCE &&
        licenseDetails &&
        licenseDetails.status === 'active'
      ) {
        items.push(
          <ColoredFlyoutMenuItem
            compact
            onClick={() => {
              setShowCancelPlanPopup(true);
            }}
            data-mixpanel="cancel-plan-actions"
          >
            <StrokeIcon size="md" name="Close" />
            <Content>Cancel plan</Content>
          </ColoredFlyoutMenuItem>,
        );
      }
      if (
        licenseDetails &&
        licenseDetails.status !== 'expired' &&
        licenseDetails.status !== 'archived'
      ) {
        if (merchantDetails?.widgetStatus) {
          items.push(
            <MenuItem
              compact
              onClick={turnOffWidget}
              data-mixpanel="widget-turn-off"
            >
              <WidgetIcon />
              Turn off widget
            </MenuItem>,
          );
        } else {
          items.push(
            <ColoredFlyoutMenuItem
              color={'#136EF8'}
              compact
              onClick={turnOnWidget}
              data-mixpanel="widget-turn-on"
            >
              <WidgetIcon />
              Turn on widget
            </ColoredFlyoutMenuItem>,
          );
        }
      }
    }
    items.push(
      <MenuItem
        compact
        onClick={() => {
          window.open('https://accessibe.com/support', '_blank');
        }}
        data-mixpanel="help-center"
      >
        <StrokeIcon size="md" name="QuestionMarkWithCircle" />
        <Content>Help center</Content>
      </MenuItem>,
    );
    items.push(
      <MenuItem compact onClick={onLogout} data-mixpanel="log-out">
        <StrokeIcon size="md" name="Logout" />
        Log out
      </MenuItem>,
    );
    return items;
  };

  const items = MenuItems();

  return (
    <Flex>
      {showCancelPlanPopup && (
        <CancelPlanPopup onClose={() => setShowCancelPlanPopup(false)} />
      )}
      {showTurnOffWidgetPopup && (
        <TurnOffWidgetPopup
          onClose={() => setShowTurnOffWidgetPopup(false)}
          onStayAccessible={handleStayAccessible}
          onTurnOffWidget={handleTurnOffWidget}
        />
      )}
      <FlyoutMenu
        align="end"
        trigger={
          <Button
            size="md"
            scale="ghost"
            variant={isMobile ? 'tertiary' : 'secondary'}
            data-mixpanel="universal-dashboard-actions"
            style={{
              border: '1.5px solid #CBD2E1',
              backgroundColor: '#FFF',
              minWidth: '32px',
              height: '32px',
              color: '#3A4B61',
            }}
          >
            <StrokeIcon
              name="Ellipsis"
              size={20}
              style={{ transform: 'rotate(90Deg)', color: '#3A4B61' }}
            />
          </Button>
        }
      >
        {items.map((item, index) => (
          <div key={index}>{item}</div>
        ))}
      </FlyoutMenu>
    </Flex>
  );
};

const MenuItem = styled(FlyoutMenuItem)`
  padding: 4px 12px;
  width: 160px;
  height: 32px;
  justify-content: center;
  span,
  p {
    font-size: 14px !important;
  }
`;

const ColoredFlyoutMenuItem = styled(MenuItem)<{ color?: string }>`
  span,
  p {
    color: ${({ color }) => color || '#b4121a'} !important;
  }
  svg path {
    stroke: ${({ color }) => color || '#b4121a'} !important;
  }
`;

export { FlyoutActionMenu };
